// src/api.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

// ============ Helpers ============ //
function setTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem("accessToken", accessToken);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
}

function getAccessToken() {
  return localStorage.getItem("accessToken");
}

function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

function clearTokens() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
}

function logout() {
  clearTokens();
  window.location.href = "/login"; 
}

// ============ Request Interceptor ============ //
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ Response Interceptor ============ //
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        const res = await axios.post(`${API_BASE_URL}/api/admin/RefreshToken`, {
          refreshToken,
        });

        const newAccessToken = res.data.accessToken;
        setTokens(newAccessToken, res.data.refreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest); 
      } catch (err) {
        logout();
      }
    }

    return Promise.reject(error);
  }
);

// ============ Auth APIs ============ //

// Login
export async function login(username, password) {
  const res = await api.post("/api/admin/Login", {
    userName: username,
    passwordHash: password,
  });

  const { accessToken, refreshToken } = res.data;
  setTokens(accessToken, refreshToken);

  return res.data;
}

// Logout
export async function logoutApi() {
  try {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await api.post("/api/admin/Logout", { refreshToken });
    }
  } catch (err) {
    console.error("Logout error:", err);
  } finally {
    logout();
  }
}

export default api;
